// components
import {
  ControlledCheckbox,
  ControlledDatePicker,
  ControlledSelect,
  ControlledTextField,
  ControlledTimePicker
} from 'components/shared/ControlledFields';
import {
  StyledControlledDatePickerContainer,
  StyledFormContainer,
  StyledIsSingleDayCheckbox
} from './LeaveRequestForm.styles';

// hooks
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import useGetEmployeeWorkAgreements from '../hooks/useGetEmployeeWorkAgreements';
import useGetPublicDutyHourTypes from '../hooks/useGetPublicDutyHourTypes';

// styles
import {
  StyledBoxFlex,
  StyledBoxFlexSmallGap
} from 'components/shared/Box/Box.styles';

// utils
import dayjs from 'dayjs';
import { t } from 'i18next';
import { toOptions } from 'utils/toOptions';

const LeaveRequestForm = ({ employeeId }) => {
  const {
    control,
    formState: { errors },
    setValue
  } = useFormContext();

  const startDate = useWatch({ control, name: 'startDate' });
  const endDate = useWatch({ control, name: 'endDate' });
  const endTime = useWatch({ control, name: 'endTime' });
  const baseStartTime = dayjs(startDate).isValid()
    ? dayjs(startDate).startOf('day')
    : dayjs().startOf('day');
  const baseEndTime = dayjs(startDate).isValid()
    ? dayjs(startDate).endOf('day')
    : dayjs().endOf('day');

  const isSingleDay = useWatch({ control, name: 'isSingleDay' });

  const validStartDate = dayjs(startDate).isValid() && startDate;
  const validEndDate = dayjs(endDate).isValid() ? endDate : null;
  const maxValidStartDate = dayjs(validStartDate).subtract(1, 'year').toDate();

  const { data: dutyHourTypes } = useGetPublicDutyHourTypes();
  const { data: EmployeeWorkAgreements } = useGetEmployeeWorkAgreements({
    startDate: maxValidStartDate,
    endDate: validEndDate,
    employeeId
  });

  const contractKindOptions =
    EmployeeWorkAgreements && EmployeeWorkAgreements.length > 0
      ? EmployeeWorkAgreements.sort((a, b) => a.nr - b.nr).map((item) => ({
          value: item?.waId,
          label: `${item.nr} - ${item.functionDescription} - ${
            item.brNameShort
          } - ${item.contractKind}${
            dayjs(item.validTo).toDate() < validStartDate
              ? ` - ${t('EmployeeLeaveRequests.contractExpiresOn')} ${dayjs(
                  item.validTo
                ).format('DD-MM-YYYY')}`
              : ''
          }`
        }))
      : [];

  useEffect(() => {
    isSingleDay
      ? setValue('endDate', baseEndTime)
      : setValue('endDate', endDate);
  }, [isSingleDay, startDate, setValue]);

  useEffect(() => {
    isSingleDay && endTime && setValue('endDate', dayjs(endTime).toDate());
  }, [isSingleDay, endTime, setValue]);

  useEffect(() => {
    !isSingleDay && setValue('startDate', null);
    !isSingleDay && setValue('endDate', null);
  }, [isSingleDay, setValue]);

  useEffect(() => {
    setValue('startDate', validStartDate);

    return () => {
      setValue('startDate', validStartDate);
    };
  }, []);

  useEffect(() => {
    setValue('startDate', dayjs().startOf('day').toDate());
  }, []);

  return (
    <StyledFormContainer>
      <StyledBoxFlexSmallGap>
        <StyledIsSingleDayCheckbox>
          <ControlledCheckbox
            control={control}
            name="isSingleDay"
            label={t('EmployeeLeaveRequests.isSingleDay')}
          />
        </StyledIsSingleDayCheckbox>
        <StyledControlledDatePickerContainer>
          <ControlledDatePicker
            required
            fullWidth
            variant="outlined"
            control={control}
            name="startDate"
            label={t('startDate')}
            error={!!errors.startDate}
            helperText={errors.startDate?.message?.toString()}
            minDate={dayjs().startOf('day').toDate()}
            onChange={(date) => {
              const startOfDay = dayjs(date).startOf('day').toDate();
              setValue('startDate', startOfDay);
            }}
          />
        </StyledControlledDatePickerContainer>

        {isSingleDay ? (
          <>
            <ControlledTimePicker
              fullWidth
              control={control}
              label={t('startTime')}
              name="startTime"
              baseDate={baseStartTime}
              onChange={(date) => {
                const startTime = dayjs(date).toDate();
                setValue('startDate', startTime);
              }}
            />

            <ControlledTimePicker
              fullWidth
              control={control}
              label={t('endTime')}
              name="endTime"
              baseDate={baseEndTime}
              onChange={(date) => {
                const endTime = dayjs(date).toDate();
                setValue('endDate', endTime);
              }}
            />
          </>
        ) : (
          <StyledBoxFlex>
            <ControlledDatePicker
              required
              fullWidth
              variant="outlined"
              control={control}
              name="endDate"
              label={t('endDate')}
              error={!!errors.endDate}
              helperText={errors.endDate?.message?.toString()}
              minDate={startDate || dayjs().endOf('day').toDate()}
              onChange={(date) => {
                const endOfDay = dayjs(date).endOf('day').toDate();
                setValue('endDate', endOfDay);
              }}
            />
          </StyledBoxFlex>
        )}
      </StyledBoxFlexSmallGap>

      <ControlledSelect
        variant="outlined"
        required
        control={control}
        name="leaveType"
        label={t('leaveType')}
        options={toOptions(dutyHourTypes ?? [], 'dutyTypeId', 'description')}
      />

      <ControlledSelect
        variant="outlined"
        required
        control={control}
        name="contractKind"
        label={t('contract')}
        options={contractKindOptions}
        disabled={!contractKindOptions.length}
      />

      <ControlledTextField
        fullWidth
        variant="outlined"
        control={control}
        name="leaveRequestNote"
        label={t('note')}
        multiline
        minRows={6}
      />
    </StyledFormContainer>
  );
};

export default LeaveRequestForm;
